import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Typography,
  Box,
  Card,
  CardContent,
  Avatar,
  Tabs,
  Tab,
  Grid,
  Chip,
} from '@mui/material';
import { Link } from 'react-router-dom';
import { useAuthStore } from '../store/authStore';
import { applicationsApi, Application } from '../api/applications';

export default function ProfilePage() {
  const { user, isAuthenticated } = useAuthStore();
  const navigate = useNavigate();
  const [tabValue, setTabValue] = useState(0);
  const [applications, setApplications] = useState<Application[]>([]);

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/');
      return;
    }

    applicationsApi.getMy().then(setApplications);
  }, [isAuthenticated, navigate]);

  if (!user) {
    return null;
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'pending':
        return 'warning';
      case 'approved':
        return 'success';
      case 'rejected':
        return 'error';
      default:
        return 'default';
    }
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'pending':
        return 'На рассмотрении';
      case 'approved':
        return 'Одобрено';
      case 'rejected':
        return 'Отклонено';
      default:
        return status;
    }
  };

  return (
    <Container maxWidth="md" sx={{ py: 6 }}>
      <Card sx={{ mb: 4 }}>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 3 }}>
            <Avatar
              src={user.avatar}
              sx={{ width: 100, height: 100 }}
              alt={`${user.firstName} ${user.lastName}`}
            />
            <Box>
              <Typography variant="h4">
                {user.firstName} {user.lastName}
              </Typography>
              <Typography variant="body1" color="text.secondary">
                {user.email || 'Email не указан'}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                VK ID: {user.vkId}
              </Typography>
            </Box>
          </Box>
        </CardContent>
      </Card>

      <Tabs value={tabValue} onChange={(_, v) => setTabValue(v)} sx={{ mb: 3 }}>
        <Tab label="Мои заявки" />
      </Tabs>

      {tabValue === 0 && (
        <Grid container spacing={3}>
          {applications.map((app) => (
            <Grid item xs={12} key={app._id}>
              <Card>
                <CardContent>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                    <Typography variant="h6" component={Link} to={`/events/${app.event._id}`}>
                      {app.event.title}
                    </Typography>
                    <Chip
                      label={getStatusLabel(app.status)}
                      color={getStatusColor(app.status)}
                      size="small"
                    />
                  </Box>

                  <Typography variant="body2" color="text.secondary" paragraph>
                    {app.event.description?.substring(0, 150)}...
                  </Typography>

                  {app.message && (
                    <Box sx={{ bgcolor: 'background.paper', p: 2, borderRadius: 1, mb: 2 }}>
                      <Typography variant="caption" color="text.secondary">
                        Ваше сообщение:
                      </Typography>
                      <Typography variant="body2">{app.message}</Typography>
                    </Box>
                  )}

                  {app.reviewComment && (
                    <Box sx={{ bgcolor: 'background.paper', p: 2, borderRadius: 1, mb: 2 }}>
                      <Typography variant="caption" color="text.secondary">
                        Комментарий модератора:
                      </Typography>
                      <Typography variant="body2">{app.reviewComment}</Typography>
                    </Box>
                  )}

                  <Typography variant="caption" color="text.secondary">
                    Дата подачи: {new Date(app.createdAt).toLocaleDateString('ru-RU')}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          ))}

          {applications.length === 0 && (
            <Grid item xs={12}>
              <Box sx={{ textAlign: 'center', py: 8 }}>
                <Typography variant="h6" color="text.secondary">
                  У вас пока нет заявок
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  Подайте заявку на участие в мероприятиях
                </Typography>
              </Box>
            </Grid>
          )}
        </Grid>
      )}
    </Container>
  );
}
